import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Users } from 'lucide-react';
import {
  Avatar,
  Badge,
  IconButton,
  Select,
  SelectItem,
  type BadgeProps,
} from '@ntconsult/aluma-react';
import type { Share, SharePermission } from '../../types';
import { ConfirmDialog } from '@common/components/ConfirmDialog';

interface PersonRowProps {
  share: Share;
  canManage: boolean;
  onChangeLevel: (level: SharePermission) => void;
  onRevoke: () => void;
}

const permissionTones: Record<SharePermission, BadgeProps['tone']> = {
  read: 'neutral',
  write: 'warning',
};

export function PersonRow({
  share,
  canManage,
  onChangeLevel,
  onRevoke,
}: PersonRowProps) {
  const { t } = useTranslation('shares');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isGroup = share.kind === 'group';
  const primaryName = isGroup
    ? t('people.groupLabel')
    : share.user?.name ?? t('people.unknownUser');
  const secondaryName = isGroup
    ? share.groupId ?? ''
    : share.user?.email ?? '';

  const levelLabels: Record<SharePermission, string> = {
    read: t('people.level.read'),
    write: t('people.level.write'),
  };

  const lockedReason = share.inherited
    ? t('people.inheritedFrom')
    : !canManage
      ? t('people.readOnlyShort')
      : null;

  return (
    <li className="flex items-center justify-between gap-3 p-3 border border-border rounded-lg">
        <div className="flex items-center gap-3 min-w-0">
          {isGroup ? (
            <span
              className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-foreground-muted shrink-0"
              aria-hidden="true"
            >
              <Users className="w-4 h-4" />
            </span>
          ) : (
            <Avatar name={primaryName} size="sm" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {primaryName}
            </p>
            {secondaryName && (
              <p
                className={`text-xs text-foreground-muted truncate ${
                  isGroup ? 'font-mono' : ''
                }`}
              >
                {secondaryName}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canManage && !share.inherited ? (
            <Select
              value={share.permission}
              onValueChange={(v) => onChangeLevel(v as SharePermission)}
              size="sm"
            >
              <SelectItem value="read">{levelLabels.read}</SelectItem>
              <SelectItem value="write">{levelLabels.write}</SelectItem>
            </Select>
          ) : (
            <Badge variant="soft" tone={permissionTones[share.permission]}>
              {levelLabels[share.permission]}
            </Badge>
          )}
          {lockedReason && (
            <Badge variant="soft" tone="neutral">
              {lockedReason}
            </Badge>
          )}
          {canManage && !share.inherited && (
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              label={t('people.remove')}
              variant="ghost"
              size="sm"
              onClick={() => setConfirmOpen(true)}
            />
          )}
        </div>

        <ConfirmDialog
          open={confirmOpen}
          title={t('people.confirmRevokeTitle', { name: primaryName })}
          description={t('people.confirmRevokeDescription')}
          confirmLabel={t('people.remove')}
          onConfirm={() => {
            setConfirmOpen(false);
            onRevoke();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
    </li>
  );
}
